/**
 * Thin fetch wrapper for the versioned API. Sessions use an HttpOnly cookie (never readable by JS);
 * the CSRF token lives only in memory and is sent on every state-changing request.
 */
export const API_BASE = "/api/v1";

export type ApiErrorBody = { error: { code: string; message: string; details?: unknown; requestId?: string } };

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
    readonly requestId?: string,
  ) {
    super(message);
  }
}

let csrfToken: string | null = null;
export function setCsrfToken(token: string | null) {
  csrfToken = token;
}

type Listener = (err: ApiError) => void;
const authListeners = new Set<Listener>();
/** Subscribe to authentication-level failures (expired session, required re-auth, MFA enrollment). */
export function onAuthError(listener: Listener) {
  authListeners.add(listener);
  return () => {
    authListeners.delete(listener);
  };
}

/** Pending re-authentication: requests that failed with REAUTH_REQUIRED wait here and are retried. */
let reauthWaiter: { promise: Promise<void>; resolve: () => void; reject: (e: unknown) => void } | null = null;
export function waitForReauth(): Promise<void> {
  if (!reauthWaiter) {
    let resolve!: () => void;
    let reject!: (e: unknown) => void;
    const promise = new Promise<void>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    reauthWaiter = { promise, resolve, reject };
  }
  return reauthWaiter.promise;
}
export function settleReauth(ok: boolean) {
  if (!reauthWaiter) return;
  if (ok) reauthWaiter.resolve();
  else reauthWaiter.reject(new ApiError(403, "REAUTH_CANCELED", "Confirmation was canceled"));
  reauthWaiter = null;
}

type Options = { method?: string; body?: unknown; signal?: AbortSignal; keepalive?: boolean; retryAfterReauth?: boolean };

export async function api<T>(path: string, opts: Options = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = { Accept: "application/json" };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (method !== "GET" && csrfToken) headers["X-CSRF-Token"] = csrfToken;
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    credentials: "same-origin",
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
    keepalive: opts.keepalive,
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : undefined;
  if (!res.ok) {
    const body = (data as ApiErrorBody | undefined)?.error;
    const err = new ApiError(res.status, body?.code ?? "HTTP_ERROR", body?.message ?? `Request failed (${res.status})`, body?.details, body?.requestId);
    if (err.code === "REAUTH_REQUIRED" && opts.retryAfterReauth !== false) {
      authListeners.forEach((l) => l(err));
      await waitForReauth();
      return api<T>(path, { ...opts, retryAfterReauth: false });
    }
    if (res.status === 401 || err.code === "MFA_ENROLLMENT_REQUIRED") authListeners.forEach((l) => l(err));
    throw err;
  }
  return data as T;
}

export const get = <T,>(path: string, signal?: AbortSignal) => api<T>(path, { signal });

/** Uploads a file as the raw request body (used for MMS attachments). */
export async function uploadFile<T>(path: string, file: Blob, opts: { method?: "POST" | "PUT"; maxSize?: string; headers?: Record<string, string> } = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: opts.method ?? "POST",
    credentials: "same-origin",
    headers: { "Content-Type": file.type || "application/octet-stream", ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}), ...(opts.headers ?? {}) },
    body: file,
  });
  const data = (await res.json().catch(() => undefined)) as (T & ApiErrorBody) | undefined;
  if (!res.ok) {
    const e = data?.error;
    throw new ApiError(res.status, e?.code ?? "HTTP_ERROR", res.status === 413 ? `That file is larger than ${opts.maxSize ?? "5 MB"}` : (e?.message ?? `Upload failed (${res.status})`), e?.details, e?.requestId);
  }
  return data as T;
}
export const post = <T,>(path: string, body?: unknown) => api<T>(path, { method: "POST", body: body ?? {} });
export const patch = <T,>(path: string, body: unknown) => api<T>(path, { method: "PATCH", body });
export const put = <T,>(path: string, body?: unknown) => api<T>(path, { method: "PUT", body: body ?? {} });
export const del = <T,>(path: string) => api<T>(path, { method: "DELETE" });

export function qs(params: Record<string, string | number | boolean | undefined | null | string[]>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "" || v === false) continue;
    if (Array.isArray(v)) v.forEach((x) => search.append(k, x));
    else search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : "";
}
